/**
 * Вспомогательные функции для работы со свойствами ArchiMate (PropertiesGroup / экземпляры Property).
 * У концептов (элементов, связей) и у модели может быть контейнер "properties"
 * с экземплярами свойств, ссылающимися на определения (PropertyDefinition).
 */

/**
 * Возвращает контейнер свойств у концепта или модели.
 *
 * @param {ModdleElement} concept — концепт (BaseElement, Relationship) или модель (Model)
 * @returns {ModdleElement|undefined} контейнер properties или undefined
 */
export function getProperties(concept) {
	if (!concept) return undefined;
	return concept.properties;
}

/**
 * Moddle/ArchiMate по спецификации хранит Property.value как массив LangString (для i18n).
 * Наша граница: в API/БД — строка, в moddle — массив. Конвертация только здесь.
 */

/** Строка → формат moddle (массив). Наш API отдаёт строку. */
export function toModdleValue(val) {
	if (val == null || val === '') return [];
	// legacy: массив или LangString
	if (Array.isArray(val))
		return val.map(v =>
			typeof v === 'object' && v?.value != null ? String(v.value) : String(v)
		);
	return [String(val)];
}

/** Moddle value[0] → строка для UI. */
function fromModdleValue(v) {
	if (v == null) return undefined;
	return typeof v === 'object' && v?.value != null ? String(v.value) : String(v);
}

/** Первое значение свойства (строка). */
export function getPropertyValue(concept, propertyDefinitionRefId) {
	const p = getProperties(concept)?.property?.find(
		pr => pr.propertyDefinitionRef?.id === propertyDefinitionRefId
	);
	const first = p?.value?.[0];
	return first != null ? fromModdleValue(first) : undefined;
}

/** Все значения (массив строк). */
export function getPropertyValues(concept, propertyDefinitionRefId) {
	const p = getProperties(concept)?.property?.find(
		pr => pr.propertyDefinitionRef?.id === propertyDefinitionRefId
	);
	return p?.value?.map(fromModdleValue) ?? [];
}

/**
 * Устанавливает значение(я) свойства. При необходимости создаёт контейнер properties и экземпляр Property.
 * Для одного значения передайте строку; для нескольких — массив строк.
 *
 * @param {ModdleElement} concept — концепт или модель
 * @param {string} propertyDefinitionRefId — идентификатор PropertyDefinition
 * @param {string|string[]} value — значение или массив значений
 * @param {Moddle} moddle — экземпляр moddle для создания Properties/Property
 * @param {ModdleElement} [propertyDefinition] — необязательный элемент PropertyDefinition (если ссылка по id не разрешается)
 */
export function setPropertyValue(
	concept,
	propertyDefinitionRefId,
	value,
	moddle,
	propertyDefinition
) {
	const values = toModdleValue(value);
	let props = concept.properties;
	if (!props) {
		props = moddle.create('archimate:Properties', {property: []});
		concept.properties = props;
	}
	if (!props.property) props.property = [];
	let prop = props.property.find(
		pr => pr.propertyDefinitionRef && pr.propertyDefinitionRef.id === propertyDefinitionRefId
	);
	if (!prop) {
		const ref = propertyDefinition ||
			(moddle.createRef &&
				moddle.createRef('archimate:PropertyDefinition', {
					id: propertyDefinitionRefId,
				})) || {id: propertyDefinitionRefId};
		prop = moddle.create('archimate:Property', {
			propertyDefinitionRef: ref,
			value: values,
		});
		props.property.push(prop);
	} else {
		prop.value = values;
	}
}

/**
 * Удаляет экземпляр свойства по идентификатору определения.
 *
 * @param {ModdleElement} concept — концепт или модель
 * @param {string} propertyDefinitionRefId — идентификатор PropertyDefinition
 * @returns {boolean} true, если свойство было удалено
 */
export function removeProperty(concept, propertyDefinitionRefId) {
	const props = getProperties(concept);
	if (!props || !props.property || !props.property.length) return false;
	const idx = props.property.findIndex(
		pr => pr.propertyDefinitionRef && pr.propertyDefinitionRef.id === propertyDefinitionRefId
	);
	if (idx === -1) return false;
	props.property.splice(idx, 1);
	return true;
}
